'use client';

/**
 * useHandoff — live-agent handoff state, composed into ChatContext.
 *
 * Sibling to useCallMode/useMessageManagement/etc (see
 * ChatContext.tsx's hook composition) — this hook owns nothing about the
 * socket or HubSpot; it is pure local React state driven by the connection
 * machine's server-event dispatch (useAssistantConnection.ts) and read by
 * page-level UI (HandoffBanner, ContactInfoCard, MessageBlock routing,
 * PromptInputBar gating).
 *
 * The phase-transition bodies below are extracted as standalone pure
 * functions (and exported) rather than inlined in the useCallback closures —
 * mirrors this codebase's convention of factoring policy into small
 * pure/tested functions (connectionReducer, turn-state.ts, send-routing.ts)
 * so the COLLECTING phase's enter/replace/exit transitions (Decision #17,
 * 2026-07-hubspot-native-handoff.md) are unit-testable without React/RTL.
 */

import { useCallback, useState } from 'react';
import {
  HandoffPhase,
  type ContactField,
  type HandoffContext,
  type HandoffDomain,
  type HandoffEndReason,
} from '@/types/chat';

/** contact_info_requested (Decision #17): enter — or re-enter — the
 *  pre-handoff contact-collection phase. ALWAYS a full replace (prior state
 *  is ignored entirely), which is what makes a repeated event for the same
 *  handoff_id "replace the fields/prefills" (the server-driven correction
 *  loop: an invalid submit, or a reload while still `pending_contact`, both
 *  just re-send this event) — there is no partial-merge case. Domain is
 *  unknown at this point (see HandoffContext.domain); it first arrives on
 *  HANDOFF_STARTED via buildWaitingHandoff below. */
export function buildCollectingHandoff(
  handoffId: string,
  fields: ContactField[]
): HandoffContext {
  return { handoffId, phase: HandoffPhase.COLLECTING, fields };
}

/** handoff_started: a new handoff begins, always in the `waiting` phase — no
 *  rep has joined yet. Also a full replace, so calling this while a
 *  COLLECTING object is current is how that phase EXITS: `fields` is dropped
 *  and `domain` (now known) is added. */
export function buildWaitingHandoff(
  handoffId: string,
  domain: HandoffDomain
): HandoffContext {
  return { handoffId, domain, phase: HandoffPhase.WAITING };
}

/** handoff_ended: pure transform to ENDED, preserving whatever the prior
 *  phase carried (domain/agentDisplayName/fields) via spread — same shape as
 *  the pre-existing inline updater, just named + exported so "ending
 *  mid-COLLECTING" (before a handoff's domain was ever learned) is directly
 *  testable. Retains the server's `end_reason` onto the context (previously
 *  dropped entirely) so recovery UI can distinguish agent_resolved from
 *  user_ended. */
export function toEndedHandoff(
  prev: HandoffContext | null,
  endReason?: HandoffEndReason
): HandoffContext | null {
  return prev ? { ...prev, phase: HandoffPhase.ENDED, endReason } : prev;
}

/** handoff_timeout: pure transform to TIMED_OUT — its own phase, NOT a fold
 *  into ENDED (see HandoffPhase's JSDoc for why). Carries no `endReason`:
 *  HANDOFF_TIMEOUT has no end_reason field on the wire, so nothing is
 *  synthesized here. Preserves prior fields via spread, same as
 *  toEndedHandoff. */
export function toTimedOutHandoff(
  prev: HandoffContext | null
): HandoffContext | null {
  return prev ? { ...prev, phase: HandoffPhase.TIMED_OUT } : prev;
}

export function useHandoff() {
  const [handoff, setHandoff] = useState<HandoffContext | null>(null);

  /** contact_info_requested: see buildCollectingHandoff above. uiState stays
   *  CHAT for this phase (the CONTACT_INFO_REQUESTED case in
   *  useAssistantConnection.ts deliberately does not set UIState.HANDOFF) —
   *  the card renders inline while Sara/the input bar stay live. */
  const contactInfoRequested = useCallback(
    (handoffId: string, fields: ContactField[]) => {
      setHandoff(buildCollectingHandoff(handoffId, fields));
    },
    []
  );

  /** handoff_started: a new handoff begins, always in the `waiting` phase —
   *  no rep has joined yet. */
  const startHandoff = useCallback(
    (handoffId: string, domain: HandoffDomain) => {
      setHandoff(buildWaitingHandoff(handoffId, domain));
    },
    []
  );

  /** agent_joined: a HubSpot rep accepted the conversation. */
  const agentJoined = useCallback((agentDisplayName: string) => {
    setHandoff((prev) =>
      prev ? { ...prev, phase: HandoffPhase.CONNECTED, agentDisplayName } : prev
    );
  }, []);

  /** agent_message: proof a human is live even if the agent_joined webhook
   *  signal (conversation.propertyChange:assignedTo) was delayed or missed —
   *  the first message while WAITING auto-promotes to CONNECTED. A message
   *  arriving after the phase is already CONNECTED/ENDED is a no-op here
   *  (rendering the message itself is handled separately, unconditionally —
   *  see insertHumanAgentMessage). */
  const agentMessageReceived = useCallback((agentDisplayName: string) => {
    setHandoff((prev) => {
      if (!prev || prev.phase !== HandoffPhase.WAITING) return prev;
      return { ...prev, phase: HandoffPhase.CONNECTED, agentDisplayName };
    });
  }, []);

  /** handoff_ended: the handoff is over (also how a COLLECTING handoff exits
   *  if the user cancels before ever submitting the contact form — see
   *  toEndedHandoff above). Kept (not nulled) so a last-tick UI read (e.g.
   *  the banner's exit animation) still sees the terminal phase; a
   *  subsequent startHandoff fully replaces it. */
  const markEnded = useCallback((endReason?: HandoffEndReason) => {
    setHandoff((prev) => toEndedHandoff(prev, endReason));
  }, []);

  /** handoff_timeout: no rep ever joined before the backend's no-agent
   *  timeout — its own phase, not ENDED (see toTimedOutHandoff above). Also
   *  how a COLLECTING handoff exits if the user never submits the contact
   *  form before the timeout fires. */
  const markTimedOut = useCallback(() => {
    setHandoff(toTimedOutHandoff);
  }, []);

  /** SESSION_CHANGED (new/resumed conversation): a stale handoff object must
   *  not leak into the next conversation's state, even though uiState alone
   *  already prevents it from driving anything (defense in depth). */
  const clearHandoff = useCallback(() => setHandoff(null), []);

  return {
    handoff,
    contactInfoRequested,
    startHandoff,
    agentJoined,
    agentMessageReceived,
    markEnded,
    markTimedOut,
    clearHandoff,
  };
}
